/* 

to run locally: uvicorn main:app --reload

[TODO] Update fetch calls to include the token:

const token = localStorage.getItem("authToken");

fetch(`${process.env.REACT_APP_API_URL}/apply_drug`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  },
  body: JSON.stringify({ organoid_id: selectedOrganoid })
}); 

[TODO] Add a logout button that clears the token and resets uiState
Add this somewhere in the UI — for example, inside the select or ready state:

{isLoggedIn && (
  <button onClick={() => {
    localStorage.removeItem("authToken");
    setIsLoggedIn(false);
    setUiState("login");
  }}>
    Logout
  </button>
)}

[TODO] Gate access to experiment setup unless isLoggedIn === true

*/


// src/App.js
import './App.css';
import { Routes, Route } from 'react-router-dom';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { SplashScreen } from './components/SplashScreen';
import LoginScreen from './components/LoginScreen';
import RegisterScreen from './components/RegisterScreen';
import QuotaScreen from './components/QuotaScreen';
import SessionWrapper from './components/SessionWrapper';

import { useBackendConfig } from './hooks/useBackendConfig'

import { OrganoidControls } from './components/OrganoidControls';

import { DemoLaasSetup } from './components/DemoLaasSetup';
import ExperimentDashboard from './components/ExperimentDashboard'; 

import { useOrganoidStatus } from './hooks/useOrganoidStatus';
import { useOrganoidData } from './hooks/useOrganoidData';

import 'chartjs-adapter-date-fns';
import { authFetch } from './utils/authFetch';

import { getReviewerConfig } from "./reviewer";


import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
} from 'chart.js';

import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  annotationPlugin
);

function App() {
  //const reviewer = getReviewerConfig();

  //const [reviewer, setReviewer] = useState(getReviewerConfig());

  const [reviewer, setReviewer] = useState(() => getReviewerConfig());
  useEffect(() => {
    const config = getReviewerConfig();
    console.log("[DEBUG] Reviewer config:", config);
    setReviewer(config);
  }, []);

  console.log("[DEBUG] Reviewer config:", reviewer);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState(null);

  const [email, setEmail] = useState(localStorage.getItem("email") || "");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [experimentsToday, setExperimentsToday] = useState(0);
  const [dailyLimit, setDailyLimit] = useState(1);
  const userId = localStorage.getItem("user_id");
  console.log("[DEBUG] Retrieved user ID from localStorage:", userId);

  const [bufferSize, setBufferSize] = useState(30); // default fallback [TODO] this is actually experiment length, needs renaming (in seconds)
  const [uiState, setUiState] = useState('login'); //<'init' | 'select' | 'setup' | 'ready' | 'running'>
  const [neuronIds, setNeuronIds] = useState([]);
  const { samplingInterval } = useBackendConfig();

  const [dataBuffer, setDataBuffer] = useState([]);
  const [spikeBuffer, setSpikeBuffer] = useState([]);
  const [drugTimestamp, setDrugTimestamp] = useState(null);

  const [status, setStatus] = useState('Waiting');
  const [phase, setPhase] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [phaseDuration, setPhaseDuration] = useState(null);
  const [baselineProgress, setBaselineProgress] = useState(0);
  const [baselineLocked, setBaselineLocked] = useState(false);

  const [autoApplyDrug, setAutoApplyDrug] = useState(false);  // check to see if these can be removed

  const [organoidOptions, setOrganoidOptions] = useState([]);
  const [protocolOptions, setProtocolOptions] = useState([]);
  const [selectedOrganoid, setSelectedOrganoid] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [logId, setLogId] = useState(null);


  const [finalRecoveryFailed, setFinalRecoveryFailed] = useState(false);
  const cancelledRef = useRef(false);


  const handleLogin = async (email, password) => {
    const isDev = false; // force not to work in develpment locally
    //const isDev = process.env.NODE_ENV === "development";
    const devToken = process.env.REACT_APP_DEV_JWT;

    let loginEmail = email;
    let loginPassword = password;

    if (isDev && devToken) {
      console.log("[LOGIN] Dev mode detected. Using token from .env");
      loginEmail = process.env.REACT_APP_DEV_EMAIL;
      loginPassword = process.env.REACT_APP_DEV_PASSWORD;
      //localStorage.setItem("authToken", devToken);
      //setIsLoggedIn(true);
      //setUiState("init");
      //return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json.error || json.detail || "Login failed");

      const { token, company, role, experiments_today, unlimited_access, daily_limit, id } = json;

      localStorage.setItem("authToken", token);
      localStorage.setItem("company", company);
      localStorage.setItem("role", role);
      localStorage.setItem("experiments_today", experiments_today);
      localStorage.setItem("unlimited_access", unlimited_access);
      localStorage.setItem("daily_limit", daily_limit);
      localStorage.setItem("user_id", id);
      localStorage.setItem("email", loginEmail);

      // ✅ Validate token before proceeding
      try {
        const test = await authFetch(`${process.env.REACT_APP_API_URL}/me`);
        if (!test.ok) throw new Error("Token rejected");

        setCompany(company);
        setRole(role);
        setExperimentsToday(experiments_today);
        setDailyLimit(daily_limit);
        const normalizedLimit = daily_limit === "unlimited" ? null : Number(daily_limit);
        setDailyLimit(normalizedLimit);

        setEmail(loginEmail);

        const isUnlimited = daily_limit === null;

        if (!isUnlimited && experiments_today >= daily_limit) {
          setUiState("quota");
        } else {
          await startSession(token);

          setIsLoggedIn(true);
          cancelledRef.current = false;
          setUiState("init");
          setSelectedProtocol(null);
          setBaselineProgress(0);
          setAutoApplyDrug(false);
        }
      } catch (err) {
          console.error("[LOGIN] Token validation failed:", err);
          setLoginError("Login failed: token rejected");
      }

    } catch (err) {
      console.error("[LOGIN] Auth error:", err);

      // ✅ Surface backend error message
      const message = err?.message || "Invalid credentials or server error";
      setLoginError(message);
    }
  };

  const handleLogout = async () => {
    console.log("[LOGOUT] Clearing auth state and resetting UI");

    if (isRecording) {
      exitExperiment(
        "user_logout",
        phase,
        spikeBuffer?.length || 0
      );
    }
    
    await endSession("manual_logout");

    cancelledRef.current = true;

    resetStatusState();
    resetDataState();

    // Clear all auth-related localStorage keys
    localStorage.removeItem("authToken");
    localStorage.removeItem("company");
    localStorage.removeItem("role");
    localStorage.removeItem("experiments_today");
    localStorage.removeItem("daily_limit");
    localStorage.removeItem("user_id");
    localStorage.removeItem("email");
    localStorage.removeItem("session_id");

    // Reset UI state
    setIsLoggedIn(false);
    setCompany(null);
    setRole(null);
    setExperimentsToday(0);
    setDailyLimit(0);
    setEmail("");
    setSelectedProtocol(null); 
    setBaselineProgress(0); 
    setAutoApplyDrug(false);
    setUiState("login");
  };

  // [enable later] persistent login across reloads 
  /*
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const storedCompany = localStorage.getItem("company");
    const storedRole = localStorage.getItem("role");

    if (token) {
      setIsLoggedIn(true);
      setCompany(storedCompany || "");
      setRole(storedRole || "");
      setUiState("init");
    }
  }, []); 
  */

  /*
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      setIsLoggedIn(true);
      setUiState("init"); // or wherever you want to land post-login
    }
  }, []);
  */

  const progressPercent = useMemo(() => {
    if (remainingSeconds == null || phaseDuration == null) return 0;
    const elapsed = phaseDuration - remainingSeconds;
    return Math.min(100, Math.round((elapsed / phaseDuration) * 100));
  }, [remainingSeconds, phaseDuration]);

  const applyDrug = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/apply_drug`, {
        method: 'POST',
        body: JSON.stringify({ organoid_id: selectedOrganoid })
      });

      if (!res.ok) throw new Error("Drug application rejected");

      const json = await res.json();
      if (json.drugTimestamp) setDrugTimestamp(json.drugTimestamp);
    } catch (err) {
      console.error("[ERROR] applyDrug failed:", err);
      setUiState("login"); // optional: redirect on auth failure
    }
  };

  /*
  useEffect(() => {
    if (autoApplyDrug && phase === 'drug_ready') {
      applyDrug();
    }
  }, [phase, autoApplyDrug]);
  */


  /* test the final data recovery failed   
  useEffect(() => {
    if (uiState === 'complete') {
      setFinalRecoveryFailed(true); // 🔧 simulate timeout
    }
  }, [uiState]);
*/

  async function startSession(token) {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/session/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({splash_ab_variant: splashKey}),
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem("session_id", data.session_id);
      } else {
        console.error("[SESSION] Failed to start session:", data);
      }
    } catch (err) {
      console.error("[SESSION] Error starting session:", err);
    }
  }



  async function endSession(reason) {
    const sessionId = localStorage.getItem("session_id");
    if (!sessionId) return;

    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/session/end`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          session_id: sessionId,
          logout_reason: reason,
          had_active_experiment: isRecording,
          active_experiment_log_id: logId || null,
          ui_state: uiState,
        }),
      });
    } catch (err) {
      console.error("[SESSION] Error ending session:", err);
    }
  }



  useEffect(() => {
    const handleBeforeUnload = () => {
      const sessionId = localStorage.getItem("session_id");
      const token = localStorage.getItem("authToken");

      if (!sessionId || !token) return;

      const payload = JSON.stringify({
        session_id: sessionId,
        logout_reason: "tab_closed",
        ui_state: uiState,
        had_active_experiment: isRecording,
        active_experiment_log_id: logId || null,
        auth_token: token,
      });

      navigator.sendBeacon(
        `${process.env.REACT_APP_API_URL}/api/session/teardown`,
        new Blob([payload], { type: "application/json" })
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [uiState, isRecording, logId]);




  async function startExperiment(selectedOrganoid, protocolName) {
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_URL}/api/experiment/run`,
        {
          method: "POST",
          body: JSON.stringify({
            organoid_id: selectedOrganoid,
            protocol_name: protocolName
          })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 403) {
          alert("You've reached your daily experiment limit. Please come back tomorrow.");
        } else {
          alert("Failed to start experiment. Please try again.");
        }
        throw new Error("Experiment start failed");
      }

      console.log("[INFO] Experiment started. Log ID:", data.log_id);
      setLogId(data.log_id);

      await authFetch(`${process.env.REACT_APP_API_URL}/start`, {
        method: "POST",
        body: JSON.stringify({ organoid_id: selectedOrganoid })
      });

      setIsRecording(true);
      setUiState("running");

    } catch (err) {
      console.error("Experiment start failed:", err);
    }
  }


  const stopExperiment = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/stop`, {
        method: 'POST',
        body: JSON.stringify({ organoid_id: selectedOrganoid })
      });

      if (!res.ok) throw new Error("Experiment stop rejected");

      console.log("[INFO] Experiment stopped successfully.");
    } catch (err) {
      console.error("[ERROR] stopExperiment failed:", err);
      setUiState("login"); // optional: redirect on auth failure
    }
  };

  async function exitExperiment(exitReason, finalPhase, finalChunkId) {
  try {
    const payload = {
      log_id: logId,
      organoid_id: selectedOrganoid,
      exit_reason: exitReason,
      final_phase: finalPhase,
      final_chunk_id: finalChunkId,
      notes: null
    };

    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/experiment/exit`,
      {
        method: "POST",
        body: JSON.stringify(payload)
      }
    );

    if (!res.ok) {
      console.error("[ERROR] exitExperiment failed:", await res.text());
      return;
    }

    console.log("[INFO] Experiment exit logged:", payload);
  } catch (err) {
    console.error("[ERROR] exitExperiment threw:", err);
  }
}


  useEffect(() => {
    if (!baselineLocked && (phase === 'baseline' || phase === 'drug_ready' || phase === 'drug')) {  // might want to chnage so that it is gated on baseline_complete from backend
      if (remainingSeconds != null && phaseDuration != null) {
        const elapsed = phaseDuration - remainingSeconds;
        const percent = Math.min(100, Math.round((elapsed / phaseDuration) * 100));
        setBaselineProgress(percent);

        // Hack - phase transistion is causing tie remaining to pass to the next phase, needs cleaning up or addressing
        // Lock progress once drug_ready is reached - note: go back and chnage "drug_ready" to baseline_complete (more relevant and general)
        // If we're entering drug_ready, force baseline to 100%
        if (phase === 'drug_ready'  || phase === 'drug') {
          setBaselineProgress(100);
          setBaselineLocked(true);
        } else {
          setBaselineProgress(percent);
        }
      }
    }
  }, [phase, remainingSeconds, phaseDuration, baselineLocked]);

  useEffect(() => {
    console.log("Baseline progress updated:", baselineProgress);
  }, [baselineProgress]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const fetchOrganoids = async () => {
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/organoids`);
        if (!res.ok) throw new Error("Organoid fetch failed");
        const data = await res.json();
        console.log("Fetched organoids:", data.organoids);
        setOrganoidOptions(data.organoids);
      } catch (err) {
        console.error("[ERROR] organoid fetch failed:", err);
        setUiState("login");
      }
    };

    const fetchProtocols = async () => {
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/protocols`);
        if (!res.ok) throw new Error("Protocol fetch failed");
        const data = await res.json();
        setProtocolOptions(data.protocols);
      } catch (err) {
        console.error("[ERROR] protocol fetch failed:", err);
        setUiState("login");
      }
    };

    fetchOrganoids();
    fetchProtocols();
  }, [isLoggedIn]);


  useEffect(() => {
    if (!isLoggedIn) return;

    if (organoidOptions.length > 0 && protocolOptions.length > 0) {
      const delay = setTimeout(() => {
        setUiState('select');
        }, 3000); // 1 second minimum splash duration - intentional to simulate connection
        return () => clearTimeout(delay);        
    }
  }, [organoidOptions, protocolOptions, isLoggedIn]);
/*
  useEffect(() => {
    if (!isLoggedIn && uiState !== 'login') {
      console.warn("[APP] Resetting uiState to 'login' due to logout");
      setUiState('login');
    }
  }, [isLoggedIn, uiState]);
*/
  useEffect(() => {
    console.log("isRecording updated:", isRecording);
  }, [isRecording]);

  useEffect(() => {
    if (uiState !== 'login') {
      setLoginError(null); // ✅ Clear lingering error when leaving login screen
    }
  }, [uiState]);

// -------------- Initialisation Splash Sequence ----------------------

  const splashVariants = {
    brain: ["Experience", "Brain-on-a-Chip"],
    lab: ["Experience", "Lab-as-a-Service"]
  };

  const variantKeys = Object.keys(splashVariants);
  const [splashKey] = useState(() => variantKeys[Math.floor(Math.random() * variantKeys.length)]);
  const splashLines = splashVariants[splashKey];


  function getRandomDelay(min = 3000, max = 6000) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  const baseSteps = [
    { message: "Allocating Resources...", duration: getRandomDelay(100, 10000) },
    { message: "Fetching Brain-on-a-Chip...", duration: getRandomDelay(3000, 6000) },
    { message: "Connecting to chip...", duration: getRandomDelay(1000, 2000) },
    { message: "Prepping experiment...", duration: getRandomDelay(2000, 6000) },
  ];

  const [setupSteps, setSetupSteps] = useState([]);
  const [setupMessage, setSetupMessage] = useState("");

  const [setupLog, setSetupLog] = useState([]);


  useEffect(() => {
    if (uiState === 'setup') {

      const token = localStorage.getItem("authToken");
      // ✅ Guard: only run setup if logged in and token exists
      if (!token || !isLoggedIn || cancelledRef.current) {
        setUiState("login");
        return;
      }

      // ⭐ Reset splash state for a clean run 
      setSetupLog([]); 
      setSetupSteps([]);

      const runSetup = async () => {
        try {
          // Trigger backend setup
          const res = await authFetch(`${process.env.REACT_APP_API_URL}/setup`, {
            method: 'POST',
            body: JSON.stringify({
              protocol_name: selectedProtocol
            })
          });

          if (!res.ok) throw new Error("Setup failed");
          const data = await res.json();

          setSelectedOrganoid(data.organoid_id);  // use backend generated organoid, unique to session (depreciate when db set up)
          if (data.neurons) {
            setNeuronIds(data.neurons);
            console.log("Setup complete. Neurons:", data.neurons);
          }
          if (data.total_duration) {
            setBufferSize(data.total_duration); // ✅ dynamically set buffer size
            console.log(`[SETUP] Buffer size set to ${data.total_duration} from protocol`);
          }

          // Build splash sequence
          const firstStep = { message: "Allocating Resources...", duration: getRandomDelay(100, 4000) };
          const steps = [firstStep];

          if (firstStep.duration > 3000) {
            steps.push({
              message: "Allocating Resources... sorry for the small delay, this will take less than a minute",
              duration: getRandomDelay(100, 8000)
            });
          }

          steps.push(...baseSteps.slice(1)); // append remaining steps
          setSetupSteps(steps);
          //setSetupMessage(steps[0].message);  // depreciate in favor of log

          // Run splash sequence
          let i = 0;
          let cancelled = false; // cancellation flag for logout
          const runStep = () => {
            if (cancelledRef.current || !isLoggedIn){
              console.warn("[SPLASH] Aborting splash sequence due to logout");
              return; // ✅ Abort splash sequence if user logs out mid-flow
            }
            if (i < steps.length) {
              const timestamp = new Date().toISOString();
              setSetupLog(prev => [...prev, { message: steps[i].message, timestamp }]);
              // setSetupMessage(steps[i].message); // depreciate in favor of log
              setTimeout(() => {
                i++;
                runStep();
              }, steps[i].duration);
            } else {
              setUiState('ready');
            }
          };
          runStep();
        } catch (err) {
          console.error("Setup error:", err);
          setUiState("login"); // fallback if token is invalid or expired
        }
      };

      runSetup();
    }
  }, [uiState, isLoggedIn]);
// ------------------------------------------------------------------------

  const { resetStatusState } =useOrganoidStatus(
    selectedOrganoid,
    setStatus,
    setPhase,
    setRemainingSeconds,
    setPhaseDuration,
    setIsRecording,
    uiState,
    setUiState,
    setDrugTimestamp,
    setFinalRecoveryFailed
  );

  const { isSyncing, resetDataState } = useOrganoidData(
    selectedOrganoid,
    isRecording,
    setDataBuffer,
    setSpikeBuffer,
    samplingInterval,
    uiState,
    setUiState,
    setFinalRecoveryFailed,
  );

  const experimentStartTime = useMemo(() => {
    const firstValid = dataBuffer.find(d => d?.timestamp && typeof d.relativeTime === 'number');
    return firstValid
      ? (Number(firstValid.timestamp) - Number(firstValid.relativeTime)) / 1000
      : null;
  }, [dataBuffer]);

  return (
    <>
      {uiState === 'login' && (
        <LoginScreen 
          handleLogin={handleLogin} 
          loginError={loginError} 
          setUiState={setUiState}
          initialEmail={reviewer?.defaultEmail || ""}
          initialPassword={reviewer?.defaultPassword || ""}
          overlay={reviewer?.overlay}
          reviewer={reviewer}
        />
      )}

      {uiState === 'register' && (
        <RegisterScreen 
          setUiState={setUiState} 
        />
      )}

      {uiState === 'quota' && (
        <QuotaScreen
          company={company}
          role={role}
          experimentsToday={experimentsToday}
          dailyLimit={dailyLimit}
          setUiState={setUiState}
          handleLogout={handleLogout}
        />
      )}

      {isLoggedIn && uiState === 'init' && (
        <SessionWrapper email={email} handleLogout={handleLogout}>
          <SplashScreen 
            theme="automation" 
            message={`Welcome${role ? ` ${role}` : ''}${company ? ` at ${company}` : ''}, preparing your Cloud Lab...`}
            splashLines={splashLines} // pass A/B variant
            ui_state={uiState}
          />
        </SessionWrapper>
      )}
      
      {isLoggedIn && uiState === 'select' && (
        <SessionWrapper email={email} handleLogout={handleLogout}>
          <DemoLaasSetup
            organoidOptions={organoidOptions}
            protocolOptions={protocolOptions}
            //selectedOrganoid={selectedOrganoid}
            selectedProtocol={selectedProtocol}
            //setSelectedOrganoid={setSelectedOrganoid}
            setSelectedProtocol={setSelectedProtocol}
            setDrugTimestamp={setDrugTimestamp}
            isRecording={isRecording}
            setUiState={setUiState}
            theme="automation"
            splashLines={splashLines} // pass A/B variant
            ui_state={uiState}
          />
        </SessionWrapper>
      )}

      {isLoggedIn && uiState === 'setup' && (
        <SessionWrapper email={email} handleLogout={handleLogout}>
          <SplashScreen 
            theme="personalization" 
            splashLines={splashLines} // pass A/B variant
            setupLog={setupLog}
            ui_state={uiState}
          />
        </SessionWrapper>
      )}

      {['ready', 'running', 'complete'].includes(uiState) && (
        <SessionWrapper email={email} handleLogout={handleLogout}>
          <>
            {isSyncing && (
              <div style={{
                position: 'fixed',
                top: 0, left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(0,0,0,0.4)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '1.5rem',
                fontWeight: 'bold',
                backdropFilter: 'blur(4px)'
              }}>
                Synchronizing with Cloud Lab…
              </div>
            )}

            <ExperimentDashboard
              startExperiment={startExperiment}
              exitExperiment={exitExperiment}
              currentUserId={userId}
              uiState={uiState}
              status={status}
              phase={phase}
              remainingSeconds={remainingSeconds}
              baselineProgress={baselineProgress}
              dataBuffer={dataBuffer}
              spikeBuffer={spikeBuffer}
              drugTimestamp={drugTimestamp}
              bufferSize={bufferSize}
              experimentStartTime={experimentStartTime}
              neuronIds={neuronIds}
              applyDrug={applyDrug}
              stopExperiment={stopExperiment}
              selectedOrganoid={selectedOrganoid}
              setIsRecording={setIsRecording}
              setUiState={setUiState}
              autoApplyDrug={autoApplyDrug}
              setAutoApplyDrug={setAutoApplyDrug}
              finalRecoveryFailed={finalRecoveryFailed}
              setFinalRecoveryFailed={setFinalRecoveryFailed}
              protocolName={selectedProtocol}
            />
          </>
        </SessionWrapper>
      )}
    </>
  );
}

console.log('Using backend:', process.env.REACT_APP_API_URL);

export default App;
